import { z } from 'zod';

// ---------------------------------------------------------------------------
// G3: GET /v1/products/:slug?lang=...&site_code=...
// Note: DB uses numeric IDs; category.id exposed as number
// ---------------------------------------------------------------------------

// WS1-T14: main_theme sub-object.
// The operator-designated main theme for this product (the categories_wallpapers
// row where is_main_theme=true AND the linked category type='theme').
// null when no main-theme designation exists for this product.
// Used by client breadcrumb builder (T8) to resolve the canonical path crumb
// without falling back to first-L1-by-sort.
export const ProductMainThemeSchema = z.object({
  /** Localized URL slug of the main-theme category (from categories_i18n.uri). */
  slug: z.string(),
  /** Localized display name (from categories_i18n.name). */
  name: z.string(),
}).nullable();
export type ProductMainTheme = z.infer<typeof ProductMainThemeSchema>;

// WS4-T7: per-product category membership entry (full M:N from categories_wallpapers).
// Used by FacetTags (T5) and similar-by-facet selection (T6) to identify
// which hub-type categories (room/style/color) a product belongs to.
// Additive — optional on the ProductResponseSchema so old cached responses remain valid.
export const ProductCategorySchema = z.object({
  /** Localized URL slug (from categories_i18n.uri). */
  slug: z.string(),
  /** Localized display name (from categories_i18n.name). */
  name: z.string(),
  /** Cat-type value (e.g. 'room', 'style', 'color', 'theme'). */
  type: z.string(),
});
export type ProductCategory = z.infer<typeof ProductCategorySchema>;

// WS4-T7: similar products response schema.
// GET /v1/products/:slug/similar?lang=...&limit=...
// Returns products sharing facets/main-theme with the given product,
// ranked main-theme → facets → featured-fallback, excluding self.
export const SimilarProductItemSchema = z.object({
  wallpaper_id: z.number().int().positive(),
  slug: z.string(),
  name: z.string(),
  og_image_url: z.string().optional(),
});
export type SimilarProductItem = z.infer<typeof SimilarProductItemSchema>;

export const SimilarProductsResponseSchema = z.object({
  items: z.array(SimilarProductItemSchema),
});
export type SimilarProductsResponse = z.infer<typeof SimilarProductsResponseSchema>;

export const ProductResponseSchema = z.object({
  id: z.number().int().positive(),
  slug: z.string(),
  lang_code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  width_px: z.number().int(),
  height_px: z.number().int(),
  original_path: z.string().nullable(),
  category: z.object({
    id: z.number().int().positive(),
    slug: z.string(),
    name: z.string(),
    seo_title: z.string().nullable(),
  }),
  variants: z.array(z.object({
    slug: z.string(),
    name: z.string(),
  })),
  seo: z.object({
    title: z.string().nullable(),
    h1: z.string().nullable(),
    description: z.string().nullable(),
    text: z.string().nullable(),
  }),
  generated_at: z.string().datetime().nullable(),
  // WS1-T14: additive — operator-designated main-theme for canonical URL resolution.
  // Optional (absent on old cached responses); null when no designation set.
  main_theme: ProductMainThemeSchema.optional(),
  // WS4-T7: additive — full M:N category membership with type.
  // Optional so older cached responses (without this field) remain valid.
  // Used by FacetTags (T5) and similar-by-facet (T6/T7).
  categories: z.array(ProductCategorySchema).optional(),
});
export type ProductResponse = z.infer<typeof ProductResponseSchema>;
